import type { Bounds, LatLng, MapCamera } from "@oshimap/domain";
import type { MapClusterNode, MapMarkerData } from "@oshimap/map-core";
import { toScreen } from "@oshimap/map-core";
import type { WebBasemapAdapter } from "./web-basemap";

/**
 * Google Maps JavaScript API 위의 BasemapProvider (OD-003: Google 이 Global Primary Basemap).
 *
 * 2026-09-11 감사: Legacy 운영은 `frontend/.env.production` 의 `VITE_GOOGLE_MAPS_API_KEY`(브라우저 키)로
 * Google 을, `VITE_NAVER_MAP_CLIENT` 로 NAVER 를 썼다(국내 우선). 2.0 은 SSOT 결정대로 Google 하나를 잇고,
 * 그 **기존 브라우저 키**를 그대로 쓴다 — 새 키를 만들지 않는다. 키는 빌드 때 `VITE_GOOGLE_MAPS_API_KEY` 로 들어온다.
 *
 * 마커는 SDK 의 Marker 가 아니라 **우리 React 핀**이다. 승인된 디자인(카테고리 아이콘·이벤트 수·저장 표시·
 * 클러스터 버튼)을 그대로 두기 위해 `OverlayView` 의 `overlayMouseTarget` pane 에 포털로 그린다 —
 * 그 pane 은 지도를 끌 때 함께 움직이고, 클릭을 받는다. 자리는 `fromLatLngToDivPixel` 로 계산한다.
 *
 * SDK 타입 패키지를 들이지 않고 쓰는 부분만 적었다. 전역 `google` 은 이 파일 밖으로 새지 않는다 (FR-MAP-10).
 */

interface GLatLng { lat(): number; lng(): number; }
interface GLatLngBounds { getNorthEast(): GLatLng; getSouthWest(): GLatLng; }
interface GPoint { x: number; y: number; }
interface GProjection { fromLatLngToDivPixel(p: GLatLng): GPoint | null; }
interface GPanes { overlayMouseTarget: HTMLElement; overlayLayer: HTMLElement; }
interface GListener { remove(): void; }
interface GMap {
  getCenter(): GLatLng | undefined; getZoom(): number | undefined; getBounds(): GLatLngBounds | undefined;
  moveCamera(c: { center?: { lat: number; lng: number }; zoom?: number }): void;
  fitBounds(b: GLatLngBounds, padding?: number): void;
  addListener(name: string, fn: () => void): GListener;
  getDiv(): HTMLElement;
}
interface GOverlayView {
  setMap(map: GMap | null): void;
  getPanes(): GPanes | null;
  getProjection(): GProjection | null;
  onAdd?: () => void; draw?: () => void; onRemove?: () => void;
}
interface GApi {
  Map: new (el: HTMLElement, opts: Record<string, unknown>) => GMap;
  LatLng: new (lat: number, lng: number) => GLatLng;
  LatLngBounds: new (sw: { lat: number; lng: number }, ne: { lat: number; lng: number }) => GLatLngBounds;
  OverlayView: new () => GOverlayView;
  event: { trigger(target: GMap, name: string): void; clearInstanceListeners(target: object): void };
  importLibrary(name: string): Promise<unknown>;
}
declare global {
  interface Window {
    google?: { maps?: GApi };
    gm_authFailure?: () => void;
    omGoogleMapsReady?: () => void;
  }
}

const SCRIPT_ID = "om-google-maps-sdk";
let loading: Promise<GApi> | null = null;

/** SDK 스크립트를 한 번만 넣는다. 키가 없으면 여기서 바로 거부한다 — 조용히 Mock 으로 가지 않는다. */
export function loadGoogleMaps(key: string): Promise<GApi> {
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (loading) return loading;
  if (!key) return Promise.reject(new Error("Google Maps 브라우저 키가 빌드에 없습니다 (VITE_GOOGLE_MAPS_API_KEY)."));
  loading = new Promise<GApi>((resolve, reject) => {
    /* SDK 의 callback 파라미터는 전역 함수 이름을 받는다 — 디버그 전역이 아니라 SDK 계약이다. */
    window.omGoogleMapsReady = () => {
      const api = window.google?.maps;
      if (api?.Map) resolve(api); else reject(new Error("Google Maps SDK 가 초기화되지 않았습니다."));
    };
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    const q = new URLSearchParams({ key, v: "weekly", language: "ko", region: "KR", loading: "async", callback: "omGoogleMapsReady" });
    s.src = `https://maps.googleapis.com/maps/api/js?${q.toString()}`;
    s.async = true;
    s.onerror = () => { loading = null; s.remove(); reject(new Error("Google Maps SDK 를 불러오지 못했습니다 (네트워크 또는 차단).")); };
    document.head.appendChild(s);
  });
  return loading;
}

export class GoogleBasemapAdapter implements WebBasemapAdapter {
  readonly name = "google";
  readonly ownsGestures = true;
  #key: string;
  #api: GApi | null = null;
  #map: GMap | null = null;
  #overlay: GOverlayView | null = null;
  #pane: HTMLElement | null = null;
  #listeners: GListener[] = [];
  #idle = new Set<() => void>();
  #view = new Set<() => void>();
  #err = new Set<(m: string) => void>();
  #markers: readonly MapMarkerData[] = [];
  #user: LatLng | null = null;
  #selected: string | null = null;
  #onCluster: ((c: MapClusterNode) => void) | null = null;
  #size = { width: 0, height: 0 };
  /* SDK 가 아직 없을 때(초기화 중)의 카메라 — project 의 fallback 에 쓴다. */
  #fallback: MapCamera = { center: { latitude: 37.5445, longitude: 127.0557 }, zoom: 14 };

  constructor(key: string) { this.#key = key; }

  async initialize(container: unknown, center: LatLng, zoom: number): Promise<void> {
    if (!(container instanceof HTMLElement)) throw new Error("컨테이너가 필요합니다");
    this.#fallback = { center, zoom };
    const api = await loadGoogleMaps(this.#key);
    this.#api = api;
    /* 키·도메인 제한 실패는 SDK 가 이 전역 훅으로 알린다. 조용히 회색 지도가 아니라 화면에 말한다. */
    window.gm_authFailure = () => this.#emitError("Google Maps 인증에 실패했습니다 — 키 또는 허용 도메인(referrer) 설정을 확인하세요.");
    this.#map = new api.Map(container, {
      center: { lat: center.latitude, lng: center.longitude }, zoom,
      disableDefaultUI: true, clickableIcons: false, gestureHandling: "greedy", keyboardShortcuts: true,
    });
    const pane = document.createElement("div");
    pane.className = "om-map__overlay";
    this.#pane = pane;
    const overlay = new api.OverlayView();
    overlay.onAdd = () => { overlay.getPanes()?.overlayMouseTarget.appendChild(pane); };
    overlay.draw = () => { for (const f of this.#view) f(); };
    overlay.onRemove = () => { pane.remove(); };
    overlay.setMap(this.#map);
    this.#overlay = overlay;
    this.#listeners.push(this.#map.addListener("idle", () => { for (const f of this.#idle) f(); }));
    /* 첫 idle 전에도 화면이 핀을 그릴 수 있게 한 번 알린다. */
    await new Promise<void>((resolve) => { const l = this.#map!.addListener("idle", () => { l.remove(); resolve(); }); });
  }

  destroy(): void {
    for (const l of this.#listeners) l.remove();
    this.#listeners = [];
    this.#overlay?.setMap(null);
    this.#overlay = null;
    if (this.#map && this.#api) this.#api.event.clearInstanceListeners(this.#map);
    /* Google Maps 에는 destroy 가 없다. 컨테이너를 비워 다음 초기화가 깨끗이 시작하게 한다. */
    const div = this.#map?.getDiv();
    if (div) div.replaceChildren();
    this.#map = null; this.#pane = null;
    this.#idle.clear(); this.#view.clear(); this.#err.clear();
  }

  setCenter(point: LatLng, zoom?: number): void {
    this.#fallback = { center: point, zoom: zoom ?? this.#fallback.zoom };
    if (!this.#map) return;
    const cur = this.getCamera();
    const sameCenter = cur && Math.abs(cur.center.latitude - point.latitude) < 1e-7 && Math.abs(cur.center.longitude - point.longitude) < 1e-7;
    const sameZoom = zoom === undefined || (cur && Math.abs(cur.zoom - zoom) < 1e-6);
    if (sameCenter && sameZoom) return;   /* idle → 상태 → setCenter 되돌이를 끊는다 */
    this.#map.moveCamera({ center: { lat: point.latitude, lng: point.longitude }, ...(zoom === undefined ? {} : { zoom }) });
  }
  getCamera(): MapCamera | null {
    const c = this.#map?.getCenter(); const z = this.#map?.getZoom();
    if (!c || z === undefined) return this.#map ? this.#fallback : null;
    return { center: { latitude: c.lat(), longitude: c.lng() }, zoom: z };
  }
  fitBounds(b: Bounds): void {
    if (!this.#map || !this.#api) return;
    this.#map.fitBounds(new this.#api.LatLngBounds({ lat: b.south, lng: b.west }, { lat: b.north, lng: b.east }));
  }
  getBounds(): Bounds | null {
    const b = this.#map?.getBounds();
    if (!b) return null;
    const ne = b.getNorthEast(); const sw = b.getSouthWest();
    return { north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() };
  }
  setViewportSize(size: { width: number; height: number }): void {
    if (size.width > 0 && size.height > 0) this.#size = size;
    if (this.#map && this.#api) this.#api.event.trigger(this.#map, "resize");
  }
  setUserLocation(p: LatLng | null): void { this.#user = p; }
  getUserLocation(): LatLng | null { return this.#user; }
  setMarkers(markers: readonly MapMarkerData[]): void { this.#markers = markers; }
  getMarkers(): readonly MapMarkerData[] { return this.#markers; }
  updateSelectedMarker(key: string | null): void { this.#selected = key; }
  getSelectedKey(): string | null { return this.#selected; }
  setMarkerClustering(): void { /* 화면이 clusterPoints 로 묶는다 */ }
  setClusterClickHandler(h: (c: MapClusterNode) => void): void { this.#onCluster = h; }
  removeMarker(id: string): void { this.#markers = this.#markers.filter((m) => m.id !== id); }
  clearMarkers(): void { this.#markers = []; }
  onIdle(fn: () => void): () => void { this.#idle.add(fn); return () => this.#idle.delete(fn); }
  onViewChanged(fn: () => void): () => void { this.#view.add(fn); return () => this.#view.delete(fn); }
  onProviderError(fn: (m: string) => void): () => void { this.#err.add(fn); return () => this.#err.delete(fn); }
  overlayElement(): HTMLElement | null { return this.#pane; }

  /** overlay pane 안에서의 픽셀 자리. 투영이 아직 없으면(첫 draw 전) Mercator 로 근사한다. */
  project(p: LatLng, size: { width: number; height: number }): { x: number; y: number } {
    const proj = this.#overlay?.getProjection();
    if (proj && this.#api) {
      const pt = proj.fromLatLngToDivPixel(new this.#api.LatLng(p.latitude, p.longitude));
      if (pt) return { x: pt.x, y: pt.y };
    }
    const cam = this.getCamera() ?? this.#fallback;
    return toScreen(p, cam.center, cam.zoom, size.width > 0 ? size : this.#size);
  }

  #emitError(msg: string): void { for (const f of this.#err) f(msg); }
}
